/**
 * Tooltip - Hover info tooltip
 */

import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/infra/utils';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  position?: TooltipPosition;
  delay?: number;
  className?: string;
  /** Layout for the wrapper (default `relative inline-flex`), for a child
   *  that must keep its own box — e.g. a block that fills its host. */
  wrapperClassName?: string;
  /** False: open on hover only. A tooltip opened by focus stays up for as
   *  long as focus stays, over whatever sits under it. Default true. */
  openOnFocus?: boolean;
}

export function Tooltip({
  content,
  children,
  position = 'top',
  delay = 200,
  className,
  wrapperClassName,
  openOnFocus = true,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    timeoutRef.current = setTimeout(() => setIsVisible(true), delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const positionClasses: Record<TooltipPosition, string> = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  const arrowClasses: Record<TooltipPosition, string> = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-card border-x-transparent border-b-transparent',
    bottom:
      'bottom-full left-1/2 -translate-x-1/2 border-b-card border-x-transparent border-t-transparent',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-card border-y-transparent border-r-transparent',
    right:
      'right-full top-1/2 -translate-y-1/2 border-r-card border-y-transparent border-l-transparent',
  };

  return (
    <div
      ref={containerRef}
      className={cn('relative inline-flex', wrapperClassName)}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={openOnFocus ? showTooltip : undefined}
      onBlur={openOnFocus ? hideTooltip : undefined}
    >
      {children}
      {isVisible && content && (
        <div
          role="tooltip"
          className={cn(
            'bg-card text-foreground border-border absolute z-50 whitespace-nowrap rounded border px-2 py-1 text-xs font-medium shadow-lg',
            'animate-in fade-in-0 zoom-in-95 duration-150',
            positionClasses[position],
            className
          )}
        >
          {content}
          <div className={cn('absolute border-4', arrowClasses[position])} />
        </div>
      )}
    </div>
  );
}

// Info tooltip with icon
import { Info } from 'lucide-react';

interface InfoTooltipProps {
  content: React.ReactNode;
  position?: TooltipPosition;
  iconSize?: number;
  className?: string;
}

export function InfoTooltip({
  content,
  position = 'top',
  iconSize = 14,
  className,
}: InfoTooltipProps) {
  return (
    <Tooltip
      content={content}
      position={position}
      className={className}
    >
      <Info
        size={iconSize}
        className="text-muted-foreground hover:text-foreground cursor-help transition-colors"
      />
    </Tooltip>
  );
}
